import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Material UI components
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Divider
} from '@mui/material';

// Material UI icons
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Warehouse as WarehouseIcon,
  LocationOn as LocationOnIcon
} from '@mui/icons-material';

// Services
import { createLocation, getLocationById, updateLocation } from '../../services/locationService';
import { getWarehouses } from '../../services/warehouseService';

const LocationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  // State
  const [formData, setFormData] = useState({
    warehouse_id: '',
    rack: '',
    shelf: '',
    barcode: ''
  });
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // Fetch warehouses and location data if in edit mode
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch warehouses
        const warehousesData = await getWarehouses();
        setWarehouses(warehousesData);

        // If in edit mode, fetch location data
        if (isEditMode) {
          const locationData = await getLocationById(id);
          setFormData({
            warehouse_id: locationData.warehouse_id,
            rack: locationData.rack,
            shelf: locationData.shelf,
            barcode: locationData.barcode
          });
          setFetchLoading(false);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
        setFetchLoading(false);
      }
    };

    fetchData();
  }, [id, isEditMode]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.warehouse_id) {
      errors.warehouse_id = 'Warehouse is required';
    }

    if (!formData.rack) {
      errors.rack = 'Rack is required';
    }

    if (!formData.shelf) {
      errors.shelf = 'Shelf is required';
    }

    if (!formData.barcode) {
      errors.barcode = 'Barcode is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditMode) {
        await updateLocation(id, formData);
      } else {
        await createLocation(formData);
      }

      navigate('/locations');
    } catch (err) {
      console.error('Error saving location:', err);
      setError(err.message || 'Failed to save location. Please try again.');
      setLoading(false);
    }
  };

  // Generate a barcode if empty
  const generateBarcode = () => {
    if (formData.warehouse_id && formData.rack && formData.shelf) {
      const warehouse = warehouses.find(w => w.id === parseInt(formData.warehouse_id));
      const warehousePrefix = warehouse ? warehouse.name.substring(0, 2).toUpperCase() : 'WH';
      const barcode = `${warehousePrefix}-${formData.rack}-${formData.shelf}-${Date.now().toString().substring(9)}`;

      setFormData({
        ...formData,
        barcode
      });

      // Clear barcode error if exists
      if (formErrors.barcode) {
        setFormErrors({
          ...formErrors,
          barcode: null
        });
      }
    } else {
      setError('Please select a warehouse and enter rack and shelf values first');
    }
  };

  if (fetchLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
        >
          Dashboard
        </Link>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/locations');
          }}
        >
          Locations
        </Link>
        <Typography color="text.primary">
          {isEditMode ? 'Edit Location' : 'Add Location'}
        </Typography>
      </Breadcrumbs>

      {/* Page title */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          {isEditMode ? 'Edit Location' : 'Add New Location'}
        </Typography>
      </Box>

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Location Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth error={Boolean(formErrors.warehouse_id)}>
                <InputLabel id="warehouse-label">Warehouse *</InputLabel>
                <Select
                  labelId="warehouse-label"
                  id="warehouse_id"
                  name="warehouse_id"
                  value={formData.warehouse_id}
                  onChange={handleChange}
                  label="Warehouse *"
                >
                  <MenuItem value="">
                    <em>Select Warehouse</em>
                  </MenuItem>
                  {warehouses.map(warehouse => (
                    <MenuItem key={warehouse.id} value={warehouse.id}>
                      {warehouse.name} ({warehouse.location})
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Select the warehouse for this location</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Rack"
                name="rack"
                value={formData.rack}
                onChange={handleChange}
                error={Boolean(formErrors.rack)}
                helperText={formErrors.rack || "Enter the rack identifier (e.g., A1)"}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Shelf"
                name="shelf"
                value={formData.shelf}
                onChange={handleChange}
                error={Boolean(formErrors.shelf)}
                helperText={formErrors.shelf || "Enter the shelf identifier (e.g., S2)"}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Barcode
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              required
              label="Barcode"
              name="barcode"
              value={formData.barcode}
              onChange={handleChange}
              error={Boolean(formErrors.barcode)}
              helperText={formErrors.barcode || "Location barcode for scanning"}
            />
            <Button
              variant="contained"
              onClick={generateBarcode}
              sx={{ height: 56 }}
            >
              Generate
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/locations')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEditMode ? 'Update Location' : 'Create Location')}
          </Button>
        </Box>
      </form>
    </Container>
  );
};

export default LocationForm;
